import {
    Box,
    chakra,
    Container,
    Text,
    HStack,
    VStack,
    Flex,
    useBreakpointValue,
    Badge,
    Heading,
} from "@chakra-ui/react";
import { useColorModeValue } from "../../../components/ui/color-mode";
import AnimatedBox from "./Animation";

const milestones = [
    {
        id: 10,
        date: "APRIL 17, 2026",
        title: "Innovation Forum at HBC",
        description: "Join us at Innovation Forum at Biberach University of Applied Sciences to gain insights at our booth!"
    },
    {
        id: 9,
        date: "NOVEMBER 20, 2025",
        title: "Startup Night at Biberach University of Applied Sciences",
        description:
            "We are excited to announce that we will be participating at the Startup Night at Biberach University of Applied Sciences. Come and visit our booth to learn more about our product and meet the team!",
    },
    {
        id: 8,
        date: "AUGUST 01, 2025", 
        title: "Scientific usage of our product",
        description:
            "In August we integrated our gas analysis system into scientific workflow at Biberach University of Applied Sciences. This is a major milestone for us, as it demonstrates the versatility and applicability of our product in various fields, especially in student research.",
    },
    {
        id: 7,
        date: "JULY 01, 2025",
        title: "Server locations in Austria and the Netherlands",
        description:
            "In July 2025 we are expanding our server locations to Austria and the Netherlands to expand our reach inside of the EU. This will allow us to provide better service in these regions.",
    },
    {
        id: 6,
        date: "MARCH 01, 2025",
        title: "Expanding our server locations",
        description:
            "In 2025 we are expanding our server locations from Germany to other countries in the EU. Stay tuned for more information.",
    },
    {
        id: 5,
        date: "OCTOBER 01, 2024",
        title: "Closed Alpha Phase",
        description:
            "Let's kickstart the closed alpha testing phase with a group of partners. By gathering their valuable feedback, we'll be able to take our product to the next level and ensure its success upon public release.",
    },
    {
        id: 4,
        date: "JULY 12, 2024",
        title: "Sandbox Demo Day",
        description:
            "We are excited to present our product at the Sandbox Demo Day in Stuttgart. After months of hard work, interesting workshops and networking, we are ready to present our product to other startups and a jury in the field of digital business models.",
    },
    {
        id: 3,
        date: "JUNE 10, 2024",
        title: "ACHEMA 2024 in Frankfurt",
        description:
            "We demonstrate our prototype at ACHEMA 2024 in Frankfurt to achieve visibility and attendance in a professional audience. Get in dialog with potential customers and spread  the idea of Open Source in the lab.",
    },
    {
        id: 2,
        date: "MAY 01, 2024",
        title: "Kick-Start Funding",
        description:
            "We have received Kick-Start funding from Gründergarage at Biberach University of Applied Sciences. This funding will help us create a prototype of our product, test it in real-world scenarios, and gather feedback. With this support, we can improve and validate our business model by testing our product with students and scientific employees.",
    },
    {
        id: 1,
        date: "FEBRUARY 14, 2024",
        title: "ASAP BW Final Pitch",
        description:
            "Making it to the ASAP BW final is an important accomplishment that showcases our innovative capabilities and potential among the leading university start-ups in Baden-Württemberg. Our participation in this event is the first step in gaining public recognition, and it opens the doors to further opportunities, marking a crucial milestone on our entrepreneurial journey.",
    },
];

const MilestoneOverview = () => {
    const isMobile = useBreakpointValue({ base: true, md: false });
    const isDesktop = useBreakpointValue({ base: false, md: true });

    return (
        <Container maxWidth="7xl" p={{ base: 2, sm: 10 }}>
            <chakra.h3 fontSize="4xl" fontWeight="bold" mb={18} textAlign="center">
                Milestones
            </chakra.h3>
            {milestones.map((milestone, index) => (
                <AnimatedBox key={milestone.id}>
                    <Flex key={milestone.id} mb="10px">
                        {/* Desktop view(left card) */}
                        {isDesktop && index % 2 === 1 && (
                            <>
                                <EmptyCard />
                                <LineWithDot />
                                <MilestoneCard {...milestone} isEvenIndex={true} />
                            </>
                        )}

                        {/* Mobile view */}
                        {isMobile && (
                            <>
                                <LineWithDot />
                                <MilestoneCard {...milestone} isEvenIndex={false} />
                            </>
                        )}

                        {/* Desktop view(right card) */}
                        {isDesktop && index % 2 === 0 && (
                            <>
                                <MilestoneCard {...milestone} isEvenIndex={false} />

                                <LineWithDot />
                                <EmptyCard />
                            </>
                        )}
                    </Flex>
                </AnimatedBox>
            ))}
        </Container>
    );
};

const Triangle = ({ isEvenId, isMobile, bgColor }) => {
    return (
        <Box
            position="absolute"
            width="0"
            height="0"
            borderColor={bgColor}
            borderStyle="solid"
            borderWidth="16px"
            left={isEvenId || isMobile ? "-16px" : "unset"}
            right={!isEvenId && !isMobile ? "-16px" : "unset"}
            transform="rotate(45deg)"
            zIndex={-1}
        />
    );
};

const MilestoneCard = ({ id, title, description, date, isEvenIndex }) => {
    const isEvenId = isEvenIndex;
    const isMobile = useBreakpointValue({ base: true, md: false });
    const currentDate = new Date();
    const milestoneDate = new Date(date);
    const isFutureMilestone = milestoneDate > currentDate;

    const bgColor = useColorModeValue("secondary.100", "primary.800");
    const futureBgColor = useColorModeValue("gray.200", "gray.700");
    const dateColorEven = "primary.400";
    const dateColorOdd = "primary.400";

    const cardBgColor = isFutureMilestone ? futureBgColor : bgColor;
    const dateTextColor = isEvenId ? dateColorEven : dateColorOdd;

    return (
        <HStack flex={1} p={{ base: 3, sm: 6 }} bg={cardBgColor} gap={5} rounded="lg" alignItems="center" pos="relative">
            <Triangle isEvenId={isEvenId} isMobile={isMobile} bgColor={cardBgColor} />
            <Box>
                <Text fontSize="lg" color={dateTextColor}>
                    {date}{" "}
                    {isFutureMilestone && (
                        <Badge ml="3" mb="3" colorPalette="blue">
                            Upcoming
                        </Badge>
                    )}
                </Text>
                <VStack gap={2} mb={3} textAlign="left">
                    <Heading fontSize="2xl" lineHeight={1.2} fontWeight="bold" w="100%">
                        {title}
                    </Heading>
                    <Text fontSize="md">{description}</Text>
                </VStack>
            </Box>
        </HStack>
    );
};

const LineWithDot = () => {
    return (
        <Flex pos="relative" alignItems="center" mr={{ base: "40px", md: "40px" }} ml={{ base: "0", md: "40px" }}>
            <chakra.span
                position="absolute"
                left="50%"
                height="calc(100% + 10px)"
                border="1px solid"
                borderColor={useColorModeValue("gray.200", "gray.700")}
                top="0px"
            ></chakra.span>
            <Box pos="relative" p="10px">
                <Box
                    pos="absolute"
                    top="0"
                    left="0"
                    bottom="0"
                    right="0"
                    width="100%"
                    height="100%"
                    backgroundSize="cover"
                    backgroundRepeat="no-repeat"
                    backgroundPosition="center center"
                    bg={useColorModeValue("gray.600", "gray.200")}
                    borderRadius="100px"
                    backgroundImage="none"
                    opacity={1}
                ></Box>
            </Box>
        </Flex>
    );
};

const EmptyCard = () => {
    return <Box flex={{ base: 0, md: 1 }} p={{ base: 0, md: 6 }} bg="transparent"></Box>;
};

export default MilestoneOverview;
